import type { PortalProduct, PortalProductPackage, PortalRequest } from "@wms/domain";
import dayjs from "dayjs";

/**
 * CW31 (31.10.2026): the pure part of the client's request form — the quantity rules of the unit (B01), the package
 * and its total in base units (B21), the values of one loaded request (B03). The server checks the same rules again.
 */

export type RequestKind = "inbound" | "outbound";

export type LineForm = {
  storage_object_id?: string | null;
  product_text?: string | null;
  package_id?: string | null;
  packages_count?: string | number | null;
  quantity?: string | number | null;
  batch_code?: string | null;
  expiry_date?: dayjs.Dayjs | null;
  comment?: string | null;
};

export type Catalog = Record<string, PortalProduct>;

/** Statuses in which the client still edits the request itself; after them a change is a change request. */
export const EDITABLE_STATUSES = ["draft", "needs_clarification"];
/** Statuses in which a change is sent to the warehouse as a change request (14.4). */
export const CHANGE_REQUEST_STATUSES = ["submitted", "in_review", "accepted"];

export const CREATE_DENIED = "Вы можете просматривать заявки. Создать заявку может сотрудник с правом создания заявок";

// ---- decimal arithmetic: «0,01 × 50» must be 0,5 exactly, as the server's Decimal -----------------------------------
type Scaled = { int: bigint; scale: number };

function toScaled(value: unknown): Scaled | null {
  if (value === null || value === undefined) return null;
  let text = String(value).trim().replace(/\s/g, "").replace(",", ".");
  if (text.startsWith(".")) text = `0${text}`;
  if (text.endsWith(".")) text = text.slice(0, -1);
  if (!/^-?\d+(\.\d+)?$/.test(text)) return null;
  const [whole, frac = ""] = text.split(".");
  return { int: BigInt(whole + frac), scale: frac.length };
}

function fromScaled({ int, scale }: Scaled): string {
  const negative = int < 0n;
  const digits = (negative ? -int : int).toString().padStart(scale + 1, "0");
  const whole = scale ? digits.slice(0, -scale) : digits;
  const frac = (scale ? digits.slice(-scale) : "").replace(/0+$/, "");
  const text = whole + (frac ? `.${frac}` : "");
  return negative && text !== "0" ? `-${text}` : text;
}

/** A typed number in the plain form the API takes: «0,50» → "0.5", «50.000» → "50"; null when it is not a number. */
export function plainQty(value: unknown): string | null {
  const scaled = toScaled(value);
  return scaled ? fromScaled(scaled) : null;
}

/** A quantity as people write it: 50, 0,5 — never 50.000. */
export function qtyText(value: unknown): string {
  const plain = plainQty(value);
  return plain === null ? String(value ?? "") : plain.replace(".", ",");
}

/** a × b without float noise: 3 × 0,333 = 0,999, not 0,9990000000000001. */
export function mulQty(a: unknown, b: unknown): string | null {
  const x = toScaled(a);
  const y = toScaled(b);
  if (!x || !y) return null;
  return fromScaled({ int: x.int * y.int, scale: x.scale + y.scale });
}

function fractionDigits(plain: string): number {
  const dot = plain.indexOf(".");
  return dot < 0 ? 0 : plain.length - dot - 1;
}

function isEmpty(value: unknown): boolean {
  return value === null || value === undefined || String(value).trim() === "";
}

/** «Для штук количество — целое число» in the words of the unit; null when the quantity fits the unit. */
export function precisionProblem(plain: string, unit: string, decimals: number): string | null {
  const digits = fractionDigits(plain);
  if (digits <= decimals) return null;
  if (decimals <= 0) return unit === "шт" ? "Для штук количество — целое число" : `Для «${unit}» количество — целое число`;
  return `Для «${unit}» можно не больше ${decimals} знаков после запятой`;
}

/**
 * CW31-B01: what is wrong with the quantity of a line, or null. 0 and a negative number are refused, nothing is put in
 * their place; the precision comes from the unit of the chosen product (pieces — whole, kilograms — three digits).
 */
export function quantityProblem(value: unknown, product?: PortalProduct): string | null {
  if (isEmpty(value)) return "Укажите количество";
  const plain = plainQty(value);
  if (plain === null) return "Введите число, например 5 или 0,5";
  if (Number(plain) <= 0) return "Количество должно быть больше 0";
  if (product && typeof product.unit_decimals === "number") return precisionProblem(plain, product.unit, product.unit_decimals);
  return null;
}

/** CW31-B21: the total of a package line in base units — "50" for 1 × «Коробка» of 50; null until both are known. */
export function packageTotal(count: unknown, pkg?: PortalProductPackage): string | null {
  if (!pkg || isEmpty(count)) return null;
  const total = mulQty(count, pkg.ratio);
  return total !== null && Number(total) > 0 ? total : null;
}

/** «1 × Коробка (50 шт) = 50 шт» — shown under the field before anything is saved. */
export function packageTotalText(count: unknown, pkg: PortalProductPackage | undefined, unit: string): string | null {
  const total = packageTotal(count, pkg);
  if (total === null || !pkg) return null;
  return `${qtyText(count)} × ${pkg.name} (${qtyText(pkg.ratio)} ${unit}) = ${qtyText(total)} ${unit}`;
}

/** The option of a package in the select: «Коробка — 50 шт». */
export function packageLabel(pkg: PortalProductPackage, unit: string): string {
  return `${pkg.name} — ${qtyText(pkg.ratio)} ${unit}`;
}

/** CW31-B21: the number of packages — positive, and the total must fit the unit (0,01 коробки по 50 = 0,5 шт — no). */
export function packagesProblem(count: unknown, pkg: PortalProductPackage | undefined, product?: PortalProduct): string | null {
  if (isEmpty(count)) return "Укажите, сколько упаковок";
  const plain = plainQty(count);
  if (plain === null) return "Введите число, например 2";
  if (Number(plain) <= 0) return "Количество должно быть больше 0";
  if (!pkg || !product || typeof product.unit_decimals !== "number") return null;
  const total = mulQty(plain, pkg.ratio);
  if (total === null) return null;
  const problem = precisionProblem(total, product.unit, product.unit_decimals);
  return problem ? `${packageTotalText(plain, pkg, product.unit)}. ${problem}` : null;
}

/** The label of a product in the select; a card of the common catalog says so. */
export function productLabel(product: PortalProduct): string {
  return `${product.code} — ${product.name} (${product.unit})${product.shared ? " · общий каталог" : ""}`;
}

/** Local search over the loaded page of the catalog: code, name, article, GTIN. */
export function productMatches(product: PortalProduct, term: string): boolean {
  const q = term.trim().toLowerCase();
  if (!q) return true;
  return [product.code, product.name, product.article, product.gtin].some((v) => !!v && v.toLowerCase().includes(q));
}

/**
 * CW31-B21: what to clear when the product of a line changes — a package of the former product never travels to the new
 * one, and its number of packages goes with it; the base quantity typed by hand stays visible and is checked again.
 */
export function afterProductChange(line: LineForm, product?: PortalProduct): Partial<LineForm> {
  if (!line.package_id) return {};
  // the card is not loaded yet: nothing is cleared on a guess
  if (line.storage_object_id && !product) return {};
  if ((product?.packages ?? []).some((p) => p.id === line.package_id)) return {};
  return { package_id: undefined, packages_count: undefined, quantity: undefined };
}

/** CW31-B21: a package chosen — the quantity is computed from it; the package cleared — its number goes too. */
export function afterPackageChange(line: LineForm): Partial<LineForm> {
  return line.package_id ? { quantity: undefined } : { packages_count: undefined };
}

/** CW31-B03: the form values of one loaded request (its full card, lines included) or of a new one. */
export function toFormValues(request: PortalRequest | null, kind: RequestKind, warehouseId?: string): Record<string, unknown> {
  return {
    kind: request?.kind ?? kind,
    warehouse_id: request?.warehouse_id ?? warehouseId,
    expected_at: request?.expected_at ? dayjs(request.expected_at) : null,
    supplier_name: request?.supplier_name ?? "",
    supplier_invoice: request?.supplier_invoice ?? "",
    recipient_name: request?.recipient_name ?? "",
    recipient_bin: request?.recipient_bin ?? "",
    delivery_kind: request?.delivery_kind ?? "delivery",
    delivery_address: request?.delivery_address ?? "",
    comment: request?.comment ?? "",
    lines: (request?.lines ?? []).map((line): LineForm => {
      const packaged = !!(line.storage_object_id && line.package_id && line.packages_count != null);
      return {
        storage_object_id: line.storage_object_id ?? undefined,
        product_text: line.product_text ?? undefined,
        package_id: packaged ? line.package_id : undefined,
        packages_count: packaged ? plainQty(line.packages_count) : undefined,
        quantity: packaged ? undefined : plainQty(line.quantity),
        batch_code: line.batch_code ?? undefined,
        expiry_date: line.expiry_date ? dayjs(line.expiry_date) : null,
        comment: line.comment ?? undefined,
      };
    }),
  };
}

/** CW31-B21: the lines as the API takes them — a package line sends the package and its number (the server computes
 * the base quantity again), any other line its base quantity; a line with a text product has no package. */
export function toPayloadLines(lines: LineForm[] | undefined, catalog: Catalog) {
  return (lines ?? []).map((line) => {
    const objectId = line.storage_object_id || null;
    const packaged = !!(objectId && line.package_id && !isEmpty(line.packages_count));
    const pkg = packaged ? catalog[objectId as string]?.packages?.find((p) => p.id === line.package_id) : undefined;
    return {
      storage_object_id: objectId,
      product_text: line.product_text || null,
      package_id: packaged ? line.package_id : null,
      packages_count: packaged ? plainQty(line.packages_count) : null,
      quantity: packaged ? (pkg ? packageTotal(line.packages_count, pkg) : null) : plainQty(line.quantity),
      batch_code: line.batch_code || null,
      expiry_date: line.expiry_date ? line.expiry_date.format("YYYY-MM-DD") : null,
      comment: line.comment || null,
    };
  });
}

/** The ids of the products of the lines that the loaded catalog does not know yet (their labels come by id). */
export function missingProductIds(request: PortalRequest | null, catalog: Catalog): string[] {
  const ids = (request?.lines ?? []).map((l) => l.storage_object_id).filter((id): id is string => !!id && !catalog[id]);
  return [...new Set(ids)].sort();
}

/**
 * CW31-B01: the errors of the server named by line and field go next to that field of the form — both the shape of the
 * request service ({field, line_no, msg}) and the shape of the schema check (loc: ["body", "lines", 0, "quantity"]).
 */
export function lineFieldErrors(errors: unknown): { name: (string | number)[]; errors: string[] }[] {
  if (!Array.isArray(errors)) return [];
  const out: { name: (string | number)[]; errors: string[] }[] = [];
  for (const raw of errors as Record<string, unknown>[]) {
    const text = String(raw.message ?? raw.msg ?? "");
    if (typeof raw.line_no === "number" && typeof raw.field === "string" && raw.field) {
      out.push({ name: ["lines", raw.line_no - 1, raw.field], errors: [text] });
      continue;
    }
    const loc = Array.isArray(raw.loc) ? raw.loc.filter((p) => p !== "body") : [];
    if (loc[0] === "lines" && typeof loc[1] === "number" && typeof loc[2] === "string") {
      out.push({ name: ["lines", loc[1], loc[2]], errors: [text] });
    }
  }
  return out;
}
